import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import {
  FiSearch, FiUser, FiMapPin, FiCheck, FiAlertTriangle,
  FiRefreshCw, FiChevronDown, FiArrowRight, FiArrowLeft, FiX,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import './homeowners.css';


const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const STATE_FULL_TO_ABBR: Record<string, string> = {
  'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
  'colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA',
  'hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA',
  'kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD',
  'massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS',
  'missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV',
  'new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY',
  'north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK',
  'oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC',
  'south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT',
  'virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
};

const toStateAbbr = (val: string): string => {
  if (!val) return '';
  const t = val.trim();
  if (US_STATES.includes(t.toUpperCase())) return t.toUpperCase();
  return STATE_FULL_TO_ABBR[t.toLowerCase()] || '';
};

const CONSTRUCTION_TYPES = [
  'Frame','Masonry','Masonry Veneer','Superior','Log','Metal',
  'Manufactured Housing','Modular','Other',
];

interface Client {
  databaseId: string;
  name: string;
  commercialName?: string;
  firstName?: string;
  lastName?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  insuredType?: number;
  eMail?: string;
  email?: string;
}

interface Property {
  databaseId: string;
  id: string;
  addressLine1?: string;
  address?: string;
  city?: string;
  state?: string;
  province?: string;
  zip?: string;
  postalCode?: string;
  active?: boolean;
}

interface HomeOwnerResult {
  constructionType: string;
  occupancy: string;
  residenceType: string;
  usageType: string;
  yearBuild: string;
  room: string;
  families: string;
  marketValue: string;
  replacementCost: string;
  totalLivingArea: string;
  count: string;
  rating: string;
  roofMaterial: string;
  primaryHeat: string;
  foundation: string;
}

interface Props {
  onGoToHistory: () => void;
}

const HomeOwnersSearch: React.FC<Props> = ({ onGoToHistory }) => {
  /* ── Client mode ── */
  const [clientMode, setClientMode] = useState<'existing' | 'new' | null>('existing');

  /* ── Existing client search — uses same cs-client-* pattern as NAICS/COI ── */
  const [clientSearch, setClientSearch]     = useState('');
  const [clientName, setClientName]         = useState('');   // display name of selected client
  const [clientDbId, setClientDbId]         = useState('');   // databaseId of selected client
  const [clientList, setClientList]         = useState<Client[]>([]);
  const [clientLoading, setClientLoading]   = useState(false);
  const [clientError, setClientError]       = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Property mode ── */
  const [propertyMode, setPropertyMode]         = useState<'existing' | 'new' | null>(null);
  const [propertyList, setPropertyList]         = useState<Property[]>([]);
  const [propertyLoading, setPropLoading]       = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showPropDrop, setShowPropDrop]         = useState(false);
  const propDropRef = useRef<HTMLDivElement>(null);

  /* ── Address form ── */
  const [newClientName, setNewClientName] = useState('');
  const [address, setAddress]             = useState('');
  const [city, setCity]                   = useState('');
  const [state, setState]                 = useState('');
  const [zip, setZip]                     = useState('');
  const [formErrors, setFormErrors]       = useState<Record<string, string>>({});

  /* ── Search / result ── */
  const [searching, setSearching]         = useState(false);
  const [result, setResult]               = useState<HomeOwnerResult | null>(null);
  const [noData, setNoData]               = useState(false);
  const [transactionId, setTransactionId] = useState('');

  /* ── Editable result fields ── */
  const [constructionType, setConstructionType] = useState('');
  const [occupancy, setOccupancy]               = useState('');
  const [residenceType, setResidenceType]       = useState('');
  const [usageType, setUsageType]               = useState('');
  const [yearBuilt, setYearBuilt]               = useState('');
  const [rooms, setRooms]                       = useState('');
  const [families, setFamilies]                 = useState('');
  const [marketValue, setMarketValue]           = useState('');
  const [replacementCost, setReplacementCost]   = useState('');
  const [totalLivingArea, setTotalLivingArea]   = useState('');
  const [roofMaterial, setRoofMaterial]         = useState('');
  const [primaryHeat, setPrimaryHeat]           = useState('');

  /* ── Resolved IDs after search (persisted for submit) ── */
  const [resolvedInsuredId, setResolvedInsuredId] = useState('');
  const [resolvedBuildingId, setResolvedBuildingId] = useState('');

  /* ── Submit to AMS ── */
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  /* ── Toast ── */
  const { showSuccess, showError } = useToast();

  /* ── Close property dropdown on outside click ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (propDropRef.current && !propDropRef.current.contains(e.target as Node)) setShowPropDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Client search — debounced, same pattern as NaicsClassificationSearch ── */
  const fetchClients = async (query: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI             || '')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  useEffect(() => {
    if (!clientSearch.trim()) { setClientList([]); return; }
    if (clientDbId) { setClientList([]); return; } // client already selected — don't re-search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setClientLoading(true);
    debounceRef.current = setTimeout(() => fetchClients(clientSearch), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch, clientDbId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Select a client from the dropdown ── */
  const onClientSelect = useCallback((c: Client) => {
    const displayName = c.insuredType === 0
      ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name
      : c.commercialName || c.name;
    setClientName(displayName);
    setClientDbId(c.databaseId);
    setClientSearch(displayName);
    setClientList([]);
    setClientError('');
    setAddress(c.addressLine1 || '');
    setCity(c.city || '');
    setState(toStateAbbr(c.state || ''));
    setZip((c.zipCode || '').replace(/\D/g, '').slice(0, 5));
    setPropertyMode(null);
    setSelectedProperty(null);
    loadProperties(c.databaseId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Load properties for selected client ── */
  const loadProperties = useCallback(async (insuredId: string) => {
    setPropLoading(true);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI             || '')}/api/nowcert/propertylist`, {
        params: { ncToken: getNcToken(), insuredId },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const all = Array.isArray(res.data) ? res.data : [];
      setPropertyList(all.filter((p: Property) => p.active === true));
    } catch { setPropertyList([]); }
    finally { setPropLoading(false); }
  }, []);

  const onPropertySelect = useCallback((p: Property) => {
    setSelectedProperty(p);
    setShowPropDrop(false);
    setAddress(p.addressLine1 || p.address || '');
    setCity(p.city || '');
    setState(toStateAbbr(p.state || p.province || ''));
    setZip((p.zip || p.postalCode || '').replace(/\D/g, '').slice(0, 5));
  }, []);

  /* ── Validation ── */
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (clientMode === 'existing' && !clientDbId) errs.client = 'Please select a client';
    if (clientMode === 'new' && !newClientName.trim()) errs.newClientName = 'Client name is required';
    /* Address fields required when form is visible */
    const addressFormVisible = clientMode === 'new' || propertyMode === 'new' || propertyMode === 'existing';
    if (addressFormVisible) {
      if (!address.trim()) errs.address = 'Address is required';
      if (!city.trim())    errs.city    = 'City is required';
      if (!state)          errs.state   = 'State is required';
      if (!/^\d{5}$/.test(zip)) errs.zip = 'Valid 5-digit zip code required';
    }
    setFormErrors(errs);
    if (errs.client) setClientError(errs.client);
    return Object.keys(errs).length === 0;
  };

  /* ── Populate result fields ── */
  const populateResult = (r: HomeOwnerResult) => {
    setConstructionType(r.constructionType || '');
    setOccupancy(r.occupancy || '');
    setResidenceType(r.residenceType || '');
    setUsageType(r.usageType || '');
    setYearBuilt(r.yearBuild || '');
    setRooms(r.room || '');
    setFamilies(r.families || '');
    setMarketValue(r.marketValue || '');
    setReplacementCost(r.replacementCost || '');
    setTotalLivingArea(r.totalLivingArea || '');
    setRoofMaterial(r.roofMaterial || '');
    setPrimaryHeat(r.primaryHeat || '');
  };

  /* ── Main search ── */
  const onSearch = useCallback(async () => {
    if (!validate()) return;
    if (!clientMode) { showError('Please select a client option first'); return; }

    setSearching(true);
    setResult(null);
    setNoData(false);
    setSubmitSuccess(false);

    try {
      let insuredId  = clientDbId;
      let buildingId = selectedProperty?.databaseId || '';

      /* Create new insured if needed */
      if (clientMode === 'new') {
        const parts = newClientName.trim().split(' ');
        const createRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI             || '')}/api/nowcert/insertinsured`,
          {
            commercialName: newClientName.trim(),
            firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '',
            dba: '', addressLine1: address, addressLine2: '',
            state, city, zipCode: zip, eMail: '', cellPhone: '', fein: '', insuredType: 1,
          },
          {
            params: { submissionId: '', nowCertsToken: getNcToken() },
            headers: { Authorization: `Bearer ${getToken()}` },
          },
        );
        insuredId = createRes.data?.database_id || '';
      }

      /* Create new property if needed */
      if (propertyMode === 'new' || clientMode === 'new') {
        const propRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI             || '')}/api/nowcert/addproperty`,
          {
            insuredDatabaseId: insuredId,
            addressLine1: address, City: city, State: state, zipCode: zip,
            propertyUse: 'Commercial', policiesDatabaseId: [],
          },
          { params: { ncToken: getNcToken() }, headers: { Authorization: `Bearer ${getToken()}` } },
        );
        buildingId = propRes.data?.databaseId || '';
      }

      /* Persist resolved IDs for use in onSubmit */
      setResolvedInsuredId(insuredId);
      setResolvedBuildingId(buildingId);

      /* CoreLogic lookup via DataFabric */
      const selectionOptions = JSON.stringify({
        useExistingClient:   clientMode === 'existing',
        useNewClient:        clientMode === 'new',
        useExistingProperty: propertyMode === 'existing',
        useNewProperty:      propertyMode === 'new' || clientMode === 'new',
      });

      const searchRes = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url          || '')}/api/HomeOwners/search`,
        {
          Country: 'US', Address: address, City: city,
          Province: state, PostalCode: parseInt(zip, 10),
          InsuredName: clientMode === 'new' ? newClientName : clientName,
          insuredId, buildingId, selectionOptionsJson: selectionOptions,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const txId = searchRes.data?.transactionId || '';
      setTransactionId(txId);

      if (searchRes.data?.success && searchRes.data?.responseJson) {
        const r: HomeOwnerResult = searchRes.data.responseJson;
        setResult(r);
        populateResult(r);
      } else {
        setNoData(true);
      }
    } catch (err: any) {
      showError(err?.response?.data?.errorMessage || 'Failed to retrieve property data');
    } finally {
      setSearching(false);
    }
  }, [clientMode, propertyMode, newClientName, clientName, clientDbId, address, city, state, zip, selectedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Submit to NowCerts ACCORD 80 ── */
  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      const ncToken = getNcToken();
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI             || '')}/api/nowcert/addaccordeighty`,
        {
          databaseId: resolvedInsuredId || clientDbId,
          buildingId: resolvedBuildingId,
          insuredDatabaseId: resolvedInsuredId || clientDbId,
          constructionTypeOtherIndicator: true,
          constructionTypeOtherCode: constructionType,
          sidingOtherUsageIndicator: true,
          sidingOtherUsageDescription: usageType,
          otherOccupancyIndicator: true,
          otherOccupancyDescription: occupancy,
          residenceTypeOtherIndicator: true,
          residenceTypeOtherDescription: residenceType,
          constructionRoofMaterialCode: roofMaterial,
          constructionBuiltYear: yearBuilt,
          marketValueAmount: marketValue,
          replacementCostAmount: replacementCost,
          totalLivingArea,
          roomCount: rooms,
          familyCount: families,
          residentialStructureFireplaceChimneyCount: '0',
          residentialStructureFireplaceHearthCount: '0',
          residentialStructureFireplacePrefabricatedCount: '0',
          residentialStructureFireplaceWoodStoveInsertCount: '0',
          heatingUnitPrimaryHeatSource: primaryHeat,
        },
        { params: { ncToken }, headers: { Authorization: `Bearer ${getToken()}` } },
      );

      if (transactionId) {
        await axios.post(
          `${(process.env.REACT_APP_DataFabric_Url          || '')}/api/HomeOwners/update`,
          {
            submittedJsonData: JSON.stringify({
              constructionType, occupancy, residenceType, usageType,
              yearBuild: yearBuilt, room: rooms, families, marketValue,
              replacementCost, totalLivingArea, roofMaterial, primaryHeat,
            }),
            transactionId,
          },
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
      }

      setSubmitSuccess(true);
      showSuccess('Property data submitted to NowCerts successfully');
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to submit to NowCerts');
    } finally {
      setSubmitLoading(false);
    }
  }, [
    resolvedInsuredId, resolvedBuildingId, clientDbId, transactionId,
    constructionType, occupancy, residenceType, usageType,
    yearBuilt, rooms, families, marketValue,
    replacementCost, totalLivingArea, roofMaterial, primaryHeat,
  ]);

  /* ── Reset ── */
  const onReset = () => {
    /* Client */
    setClientMode('existing');
    setClientSearch(''); setClientName(''); setClientDbId('');
    setClientList([]); setClientError('');
    setNewClientName('');
    /* Property */
    setPropertyMode(null);
    setSelectedProperty(null); setPropertyList([]);
    /* Address */
    setAddress(''); setCity(''); setState(''); setZip('');
    setFormErrors({});
    /* Result fields */
    setConstructionType(''); setOccupancy(''); setResidenceType('');
    setUsageType(''); setYearBuilt(''); setRooms('');
    setFamilies(''); setMarketValue(''); setReplacementCost('');
    setTotalLivingArea(''); setRoofMaterial(''); setPrimaryHeat('');
    /* State */
    setResult(null); setNoData(false); setSubmitSuccess(false);
    setTransactionId('');
    setResolvedInsuredId(''); setResolvedBuildingId('');
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div
        className="ho-search"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
      >


        {/* ── Unified card: Steps 1 → 2 → 3 — hidden once result arrives ── */}
        {!result && !noData && <div className="ho-card ho-card--form">

          {/* Step 1 — Client */}
          <div className="ho-step-label">
            <span className="ho-step-num">1</span> Client
          </div>
          <div className="ho-toggle-group">
            <button
              className={`ho-toggle${clientMode === 'existing' ? ' ho-toggle--active' : ''}`}
              onClick={() => {
                setClientMode('existing');
                setClientSearch(''); setClientName(''); setClientDbId('');
                setClientList([]);
                setPropertyMode(null);
              }}
            >
              <FiSearch size={14} /> Use Existing Client
            </button>
            <button
              className={`ho-toggle${clientMode === 'new' ? ' ho-toggle--active' : ''}`}
              onClick={() => {
                setClientMode('new');
                setClientSearch(''); setClientName(''); setClientDbId('');
                setClientList([]);
                setPropertyMode(null);
              }}
            >
              <FiUser size={14} /> Create New Client
            </button>
          </div>

          {/* Existing client search */}
          {clientMode === 'existing' && (
            <div className="cs-client-search">
              <div
                className="cs-client-search__input-wrap"
                style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}
              >
                <FiSearch size={15} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  type="text"
                  placeholder="Search by client name…"
                  value={clientSearch}
                  onChange={e => {
                    setClientSearch(e.target.value);
                    if (clientError) setClientError('');
                    if (clientName) { setClientName(''); setClientDbId(''); }
                  }}
                />
                {clientLoading && <span className="cs-client-search__spinner" />}
                {clientName && !clientLoading && (
                  <button
                    className="cs-client-search__clear"
                    title="Clear"
                    onClick={() => {
                      setClientSearch(''); setClientName(''); setClientDbId('');
                      setClientList([]); setClientError('');
                      setPropertyMode(null); setSelectedProperty(null); setPropertyList([]);
                      setAddress(''); setCity(''); setState(''); setZip('');
                    }}
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              {clientList.length > 0 && (
                <ul className="cs-client-dropdown">
                  {clientList.map(c => {
                    const name = c.insuredType === 0
                      ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name
                      : c.commercialName || c.name;
                    return (
                      <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => onClientSelect(c)}>
                        <span className="cs-client-dropdown__name">{name}</span>
                        {c.addressLine1 && (
                          <span className="cs-client-dropdown__email">{c.addressLine1}, {c.city}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {clientSearch.trim() && !clientLoading && clientList.length === 0 && !clientName && (
                <ul className="cs-client-dropdown">
                  <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>
                    No clients found
                  </li>
                </ul>
              )}

              {clientError && (
                <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{clientError}</span>
              )}
            </div>
          )}

          {/* New client name input */}
          {clientMode === 'new' && (
            <div className="cs-client-search">
              <div
                className="cs-client-search__input-wrap"
                style={{ borderColor: formErrors.newClientName ? 'var(--accent-red, #ef4444)' : undefined }}
              >
                <FiUser size={15} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  type="text"
                  placeholder="Client / Business Name"
                  value={newClientName}
                  maxLength={150}
                  onChange={e => setNewClientName(e.target.value)}
                />
              </div>
              {formErrors.newClientName && (
                <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{formErrors.newClientName}</span>
              )}
            </div>
          )}

          {/* ── Step 2: Property (existing client only) ── */}
          {clientMode === 'existing' && clientDbId && (
            <>
              <div className="ho-step-divider" />
              <div className="ho-step-label">
                <span className="ho-step-num">2</span> Property
              </div>
              <div className="ho-toggle-group">
                <button
                  className={`ho-toggle${propertyMode === 'existing' ? ' ho-toggle--active' : ''}`}
                  onClick={() => setPropertyMode('existing')}
                >
                  <FiMapPin size={14} /> Use Existing Property
                </button>
                <button
                  className={`ho-toggle${propertyMode === 'new' ? ' ho-toggle--active' : ''}`}
                  onClick={() => { setPropertyMode('new'); setSelectedProperty(null); }}
                >
                  <FiMapPin size={14} /> Add New Property
                </button>
              </div>

              {propertyMode === 'existing' && (
                <div style={{ position: 'relative' }} ref={propDropRef}>
                  <div
                    className="cs-client-search__input-wrap"
                    style={{ cursor: 'pointer' }}
                    onClick={() => !propertyLoading && setShowPropDrop(p => !p)}
                  >
                    <FiMapPin size={14} className="cs-client-search__icon" />
                    <span className="cs-client-search__input" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                      {selectedProperty
                        ? `${selectedProperty.addressLine1 || selectedProperty.address}, ${selectedProperty.city}`
                        : propertyLoading ? 'Loading properties…' : 'Select a property'}
                    </span>
                    <FiChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginRight: 10 }} />
                  </div>
                  {showPropDrop && propertyList.length > 0 && (
                    <ul className="cs-client-dropdown" style={{ marginTop: 2 }}>
                      {propertyList.map(p => (
                        <li key={p.databaseId || p.id} className="cs-client-dropdown__item" onClick={() => onPropertySelect(p)}>
                          <span className="cs-client-dropdown__name">{p.addressLine1 || p.address}</span>
                          <span className="cs-client-dropdown__email">{p.city}, {p.state || p.province} {p.zip || p.postalCode}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {selectedProperty && (
                    <span className="ho-selected-hint"><FiCheck size={11} /> {selectedProperty.addressLine1 || selectedProperty.address}</span>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Step 3: Address Form ── */}
          {(clientMode === 'new' || propertyMode === 'new' || propertyMode === 'existing') && (
            <>
              <div className="ho-step-divider" />
              <div className="ho-step-label">
                <span className="ho-step-num">{clientMode === 'new' ? '2' : '3'}</span>
                Property Address
              </div>
              <div className="ho-form-grid">
                <div className="ho-form-field ho-form-field--full">
                  <label className="ho-form-label">Address Line 1</label>
                  <div
                    className="cs-client-search__input-wrap"
                    style={{ borderColor: formErrors.address ? 'var(--accent-red, #ef4444)' : undefined }}
                  >
                    <input
                      className="cs-client-search__input"
                      type="text" placeholder="123 Main Street"
                      value={address} maxLength={255}
                      onChange={e => setAddress(e.target.value)}
                    />
                  </div>
                  {formErrors.address && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.address}</span>}
                </div>

                <div className="ho-form-field">
                  <label className="ho-form-label">City</label>
                  <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.city ? 'var(--accent-red, #ef4444)' : undefined }}>
                    <input className="cs-client-search__input" type="text" placeholder="City" value={city} maxLength={50} onChange={e => setCity(e.target.value)} />
                  </div>
                  {formErrors.city && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.city}</span>}
                </div>

                <div className="ho-form-field">
                  <label className="ho-form-label">State</label>
                  <div className="ho-select-wrap">
                    <select className={`ho-select${formErrors.state ? ' ho-input--error' : ''}`} value={state} onChange={e => setState(e.target.value)}>
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {formErrors.state && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.state}</span>}
                </div>

                <div className="ho-form-field">
                  <label className="ho-form-label">Zip Code</label>
                  <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.zip ? 'var(--accent-red, #ef4444)' : undefined }}>
                    <input className="cs-client-search__input" type="text" placeholder="12345" value={zip} maxLength={5} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} />
                  </div>
                  {formErrors.zip && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.zip}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="cs-browse-btn" onClick={onSearch} disabled={searching}>
                  {searching ? <FiRefreshCw size={15} className="ho-spin" /> : 'Get Home Owner Data'}
                </button>
              </div>
            </>
          )}

        </div>}

        {/* ── Result: No Data ── */}
        {noData && (
          <motion.div className="ho-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button className="ho-back-btn" onClick={onReset}>
              <FiArrowLeft size={14} /> Back
            </button>
            <div className="ho-card--notice" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px', borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <FiAlertTriangle size={18} className="ho-notice-icon" />
              <div>
                <div className="ho-notice-title">No property data found</div>
                <div className="ho-notice-sub">CoreLogic returned no records for this address.</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Result: Property Detail Form ── */}
        {result && (
          <motion.div className="ho-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            {/* Back button — top left */}
            <div className="ho-result-topbar">
              <button className="ho-back-btn" onClick={onReset}>
                <FiArrowLeft size={14} /> Back
              </button>
              {submitSuccess && (
                <span className="ho-badge ho-badge--success"><FiCheck size={11} /> Submitted to NowCerts</span>
              )}
            </div>

            <div className="ho-form-grid">
              <div className="ho-form-field">
                <label className="ho-form-label">Construction Type</label>
                <div className="ho-select-wrap">
                  <select className="ho-select" value={constructionType} onChange={e => setConstructionType(e.target.value)} disabled={submitSuccess}>
                    <option value="">Select Construction Type </option>
                    {CONSTRUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {([
                ['Occupancy',       occupancy,       setOccupancy],
                ['Residence Type',  residenceType,   setResidenceType],
                ['Usage Type',      usageType,       setUsageType],
                ['Year Built',      yearBuilt,       setYearBuilt],
                ['Rooms',           rooms,           setRooms],
                ['Families',        families,        setFamilies],
                ['Market Value',    marketValue,     setMarketValue],
                ['Replacement Cost',replacementCost, setReplacementCost],
                ['Total Living Area',totalLivingArea,setTotalLivingArea],
                ['Roof Material',   roofMaterial,    setRoofMaterial],
                ['Primary Heat',    primaryHeat,     setPrimaryHeat],
              ] as [string, string, (v: string) => void][]).map(([label, val, setter]) => (
                <div className="ho-form-field" key={label}>
                  <label className="ho-form-label">{label}</label>
                  <div className="cs-client-search__input-wrap">
                    <input className="cs-client-search__input" value={val} placeholder={`Enter ${label}`} onChange={e => setter(e.target.value)} disabled={submitSuccess} />
                  </div>
                </div>
              ))}
            </div>

            {!submitSuccess && (
              <div className="ho-result-actions" style={{ justifyContent: 'flex-end' }}>
                <button className="cs-browse-btn cs-browse-btn--sm" onClick={onSubmit} disabled={submitLoading}>
                  {submitLoading
                    ? <><FiRefreshCw size={13} className="ho-spin" /> Submitting…</>
                    : <> Submit</>}
                </button>
              </div>
            )}

            {submitSuccess && (
              <div className="ho-result-actions" style={{ justifyContent: 'flex-end' }}>
                <button className="ho-link-btn" onClick={onGoToHistory}>
                  View in History <FiArrowRight size={13} />
                </button>
                <button className="ho-link-btn" onClick={onReset}>New Search</button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </>
  );
};

export default HomeOwnersSearch;

